/**
 * Field Help — Social Media Posts
 *
 * Keys MUST match the field ids in
 * `features/social-media-posts/components/EnhancedSocialMediaPostsConfig.ts` so
 * the "?" popover attaches to the right column / editor row / card field.
 *
 * A social_media_post belongs to exactly ONE brand (WTC or Schooley Mitchell,
 * see MARKETING_BRANDS — brands are rows in marketing_brands) and is published to its target platforms via the
 * per-platform Zapier pipeline. Media lives in the social-media-assets public
 * bucket (the Zapier-fetchable URL) with Drive provenance for library reuse.
 */

import type { EntityFieldHelp } from "./types";

export const socialMediaPostFieldHelp: EntityFieldHelp = {
  title: {
    title: "Title",
    description: "An internal label for the post — not published anywhere.",
    usage: "Use it to find the post in lists and on the marketing calendar. If left blank, the post body is used as the display name.",
  },
  body: {
    title: "Body / Caption",
    description: "The caption or copy published with the post.",
    usage: "This is the text that goes out to each platform. The caption assistant can draft or spell-check it on brand; YouTube uses the separate title/description instead.",
  },
  status: {
    title: "Status",
    description: "Where the post is in its lifecycle: Draft → Scheduled → Publishing → Posted (or Partially Failed / Failed / Canceled).",
    usage: "Set by the publishing pipeline, not edited freely. 'Partially Failed' means some platforms posted and some didn't — check the Error column.",
    relatedTo: "The scheduling cron moves Scheduled posts to Publishing, then to a terminal status once every platform has been attempted.",
  },
  brand: {
    title: "Brand",
    description: "Which marketing brand the post belongs to — West Tennessee Consulting or Schooley Mitchell.",
    usage: "Brand decides which Zapier account, Google Drive asset folder, and voice are used. A post is always exactly one brand.",
    relatedTo: "A brand can only publish to platforms it has connected channels for — manage them under Marketing → Brands (see MARKETING_BRANDS).",
  },
  platforms: {
    title: "Platforms",
    description: "The social networks this post targets: Facebook, X (Twitter), Google Business, Instagram, Bluesky, and/or YouTube.",
    usage: "Each selected platform gets its own tailored payload. Facebook, X, and Google Business each send a separate request (routed by Buffer channel), so choosing all three posts to all three.",
  },
  media_kind: {
    title: "Media",
    description: "Whether the post carries no media, an image, or a video.",
    usage: "Instagram requires image or video; YouTube requires a video. X, Facebook, Bluesky, and Google Business can be text-only.",
  },
  media_url: {
    title: "Media URL",
    description: "The public URL of the post's image or video that Zapier fetches at publish time.",
    usage: "Auto-filled when you upload a file, generate an image, or pick an asset from the Drive library — the file is re-hosted at an unguessable public path.",
    relatedTo: "Populated from an upload, the Bedrock image generator, or a Google Drive asset (materialized into the social-media-assets bucket).",
  },
  thumbnail_url: {
    title: "Thumbnail URL",
    description: "Optional thumbnail image, used by YouTube.",
    usage: "Only relevant for YouTube video posts; ignored by the other platforms.",
  },
  tags: {
    title: "Tags / Hashtags",
    description: "Hashtags (without the '#') applied to the post.",
    usage: "Formatted correctly per platform automatically — a plain array for Buffer, space-separated #tags for Instagram, comma-separated for YouTube.",
  },
  location: {
    title: "Location",
    description: "Optional human-readable location for the post.",
    usage: "Used by Instagram; ignored by other platforms.",
  },
  scheduled_at: {
    title: "Scheduled For",
    description: "When the post is scheduled to publish.",
    usage: "The scheduling cron publishes the post once this time passes. Leave empty for drafts or on-demand posts.",
  },
  published_at: {
    title: "Published At",
    description: "When the post actually went out.",
    usage: "Set by the pipeline after a successful (or partial) publish. Empty until the post has been attempted.",
  },
  company_id: {
    title: "Company",
    description: "Optional CRM company this post is about (e.g. a client who approved being featured).",
    usage: "Links the post to a company so it shows in that company's activity. Purely optional context.",
    relatedTo: "Reuses the standard company relationship — the post appears in the company's Activity tab.",
  },
  contact_id: {
    title: "Contact",
    description: "Optional CRM contact this post is about.",
    usage: "Links the post to a contact for context and activity. Optional.",
    relatedTo: "Reuses the standard contact relationship — the post appears in the contact's Activity tab.",
  },
  error_summary: {
    title: "Error",
    description: "A summary of any platform failures from the last publish attempt.",
    usage: "Populated when status is Failed or Partially Failed — tells you which platforms rejected the post and why, so you can fix and retry.",
  },
};
